import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Switch, InputNumber, Space, Tag, Select, App } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, GlobalOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDepositOptions, addCurrency, updateCurrency, deleteCurrency, addNetwork, updateNetwork, deleteNetwork } from '../../store/slices/depositSlice';
import Loadingpage from "../Loadingpage/Loadingpage";


const CryptoSettings = () => {
    const { modal, message } = App.useApp();
    const dispatch = useDispatch();
    const { currencies, networks, loading } = useSelector((state) => state.deposit);
    const cryptoList = currencies.filter(m => m.type === 'crypto');

    const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false);
    const [isNetworkModalOpen, setIsNetworkModalOpen] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const [selectedNetwork, setSelectedNetwork] = useState(null);
    const [page, setPage] = useState({ current: 1, pageSize: 10 });
    const [form] = Form.useForm();
    const [networkForm] = Form.useForm();

    useEffect(() => {
        dispatch(fetchDepositOptions());
    }, [dispatch]);

    const handleCurrencySubmit = async (values) => {
        try {
            if (selectedCurrency && !isNetworkModalOpen) {
                await dispatch(updateCurrency({ id: selectedCurrency._id, data: values })).unwrap();
                message.success('Currency updated successfully');
            } else {
                await dispatch(addCurrency({ ...values, type: 'crypto' })).unwrap();
                message.success('Currency added successfully');
            }
            setIsCurrencyModalOpen(false);
            form.resetFields();
            setSelectedCurrency(null);
            dispatch(fetchDepositOptions());
        } catch (err) {
            message.error(err.message || 'Failed to save currency');
        }
    };

    const handleDeleteCurrency = (id) => {
        modal.confirm({
            title: 'Are you sure you want to delete this currency?',
            icon: <ExclamationCircleOutlined />,
            content: 'This will also delete all associated networks.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            async onOk() {
                try {
                    await dispatch(deleteCurrency(id)).unwrap();
                    message.success('Currency deleted successfully');
                    dispatch(fetchDepositOptions());
                } catch (err) {
                    message.error(err.message || 'Failed to delete currency');
                }
            },
        });
    };

    const handleNetworkSubmit = async (values) => {
        try {
            if (selectedNetwork) {
                await dispatch(updateNetwork({ id: selectedNetwork._id, data: values })).unwrap();
                message.success('Network updated successfully');
            } else {
                await dispatch(addNetwork({ ...values, currency_id: selectedCurrency._id })).unwrap();
                message.success('Network added successfully');
            }
            setIsNetworkModalOpen(false);
            networkForm.resetFields();
            setSelectedNetwork(null);
            dispatch(fetchDepositOptions());
        } catch (err) {
            message.error(err.message || 'Failed to save network');
        }
    };

    const handleDeleteNetwork = (id) => {
        modal.confirm({
            title: 'Are you sure you want to delete this network?',
            icon: <ExclamationCircleOutlined />,
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            async onOk() {
                try {
                    await dispatch(deleteNetwork(id)).unwrap();
                    message.success('Network deleted successfully');
                    dispatch(fetchDepositOptions());
                } catch (err) {
                    message.error(err.message || 'Failed to delete network');
                }
            },
        });
    };

    const currencyColumns = [
        { title: 'Currency', dataIndex: 'name', key: 'name', render: (t) => <span style={{ color: '#fff' }}>{t?.toUpperCase()}</span> },
        {
            title: 'Networks', key: 'networks_count', render: (_, record) => (
                <Tag color="#FC4420" style={{ borderRadius: '4px' }}>
                    {networks.filter(n => n.currency_id === record._id).length} Networks
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<GlobalOutlined />}
                        onClick={() => {
                            setSelectedCurrency(record);
                            setIsNetworkModalOpen(true);
                            networkForm.resetFields();
                        }}
                        style={{ backgroundColor: '#FC4420', borderColor: '#FC4420' }}
                    >
                        Add Network
                    </Button>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                            setSelectedCurrency(record);
                            form.setFieldsValue({ name: record.name });
                            setIsCurrencyModalOpen(true);
                        }}
                        style={{ color: '#FC4420' }}
                        type="link"
                    />
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteCurrency(record._id)}
                        type="link"
                    />
                </Space>
            ),
        },
    ];

    const expandedRowRender = (currency) => {
        setSelectedCurrency(currency);
        const currencyNetworks = networks.filter(n => n.currency_id === currency._id);
        const columns = [
            { title: 'Network Name', dataIndex: 'network', key: 'network', render: (v) => <span style={{ color: '#fff' }}>{v}</span> },
            { title: 'Symbol', dataIndex: 'symbol', key: 'symbol', render: (v) => <Tag color="#FC4420">{v}</Tag> },
            {
                title: 'Deposit',
                dataIndex: 'deposit_enabled',
                key: 'deposit_enabled',
                render: (enabled) => <Switch checked={enabled} disabled />
            },
            {
                title: 'Withdraw',
                dataIndex: 'withdrawal_enabled',
                key: 'withdrawal_enabled',
                render: (enabled) => <Switch checked={enabled} disabled />
            },
            { title: 'Min Deposit', dataIndex: 'min_deposit', key: 'min_deposit', render: (v) => <span style={{ color: '#fff' }}>{v}</span> },
            { title: 'Min Withdraw', dataIndex: 'min_withdraw', key: 'min_withdraw', render: (v) => <span style={{ color: '#fff' }}>{v}</span> },
            {
                title: 'Address',
                dataIndex: 'address',
                key: 'address',
                render: (addr) => (
                    <Space direction="vertical">
                        <span style={{ fontSize: '11px', wordBreak: 'break-all', maxWidth: '200px', color: '#fff' }}>{addr}</span>
                        <div className="qr-code-box">
                            <QRCodeSVG value={addr || ''} size={60} />
                        </div>
                    </Space>
                )
            },
            {
                title: 'Action',
                key: 'operation',
                render: (_, record) => (
                    <Space size="middle">
                        <Button
                            type="link"
                            style={{ color: '#FC4420' }}
                            onClick={() => {
                                setSelectedNetwork(record);
                                networkForm.setFieldsValue(record);
                                setIsNetworkModalOpen(true);
                            }}
                        >
                            Edit
                        </Button>
                        <Button
                            type="link"
                            danger
                            onClick={() => handleDeleteNetwork(record._id)}
                        >
                            Delete
                        </Button>
                    </Space>
                ),
            },
        ];

        return (
            <Table
                columns={columns}
                dataSource={currencyNetworks}
                pagination={{
                    pageSize: 10,
                    className: "pagination-clr",
                }}
                size="small"
                rowKey="_id"
                className="table-clr"
                scroll={{ x: 'max-content' }}
            />
        );
    };

    return (
        <div className="p-20">
            <Loadingpage />
            <div className="card-clr" style={{ padding: '20px', borderRadius: '16px', border: '1px solid #FC442080' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ color: '#fff', margin: 0 }}>Crypto Deposit Settings</h3>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setSelectedCurrency(null);
                            form.resetFields();
                            setIsCurrencyModalOpen(true);
                        }}
                        style={{ backgroundColor: '#FC4420', borderColor: '#FC4420' }}
                    >
                        Add Currency
                    </Button>
                </div>

                <Table
                    columns={currencyColumns}
                    dataSource={cryptoList}
                    loading={{
                        spinning: loading,
                        indicator: <Loadingpage isLoading={loading} />,
                    }}
                    pagination={{
                        total: cryptoList.length,
                        current: page.current,
                        pageSize: page.pageSize,
                        onChange: (p, s) => setPage({ current: p, pageSize: s }),
                        className: "pagination-clr",
                    }}
                    expandable={{ expandedRowRender }}
                    rowKey="_id"
                    className="table-clr crypto-settings"
                    scroll={{ x: 'max-content' }}
                />

                {/* Currency Modal */}
                <Modal
                    title={selectedCurrency && !isNetworkModalOpen ? "Edit Crypto Currency" : "Add New Crypto Currency"}
                    open={isCurrencyModalOpen}
                    onCancel={() => {
                        setIsCurrencyModalOpen(false);
                        setSelectedCurrency(null);
                    }}
                    onOk={() => form.submit()}
                    className="custom-modal"
                    centered
                >
                    <Form form={form} layout="vertical" onFinish={handleCurrencySubmit}>
                        <Form.Item normalize={(value) => value?.toUpperCase()} name="name" label="Currency Name" rules={[{ required: true }]}>
                            <Input placeholder="e.g. Bitcoin" className="input-cls" />
                        </Form.Item>
                    </Form>
                </Modal>

                {/* Network Modal */}
                <Modal
                    title={selectedNetwork ? `Edit Network for ${selectedCurrency?.name?.toUpperCase()}` : `Add Network for ${selectedCurrency?.name?.toUpperCase()}`}
                    open={isNetworkModalOpen}
                    onCancel={() => {
                        setIsNetworkModalOpen(false);
                        setSelectedNetwork(null);
                    }}
                    onOk={() => networkForm.submit()}
                    width={600}
                    className="custom-modal"
                    centered
                >
                    <Form form={networkForm} layout="vertical" onFinish={handleNetworkSubmit} initialValues={{ deposit_enabled: true, withdrawal_enabled: true }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <Form.Item name="network" label="Network Name" rules={[{ required: true }]}>
                                <Input placeholder="e.g. ERC20" className="input-cls" />
                            </Form.Item>
                            <Form.Item name="symbol" label="Network Symbol" rules={[{ required: true }]}>
                                <Input placeholder="e.g. USDT-ERC20" className="input-cls" />
                            </Form.Item>
                            <Form.Item name="min_deposit" label="Min Deposit" rules={[{ required: true }]}>
                                <InputNumber style={{ width: '100%', backgroundColor: '#FC442033', borderColor: "#424242", }} min={0} className="input-cls number-cls" />
                            </Form.Item>
                            <Form.Item name="min_withdraw" label="Min Withdraw" rules={[{ required: true }]}>
                                <InputNumber style={{ width: '100%', backgroundColor: '#FC442033', borderColor: "#424242", }} min={0} className="input-cls number-cls" />
                            </Form.Item>
                            <div>
                                <Form.Item name="deposit_enabled" label="Enable Deposit" valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                                <Form.Item name="withdrawal_enabled" label="Enable Withdraw" valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                            </div>
                            <Form.Item name="address" label="Wallet Address" rules={[{ required: true }]}>
                                <Input placeholder="Enter wallet address" className="input-cls" />
                            </Form.Item>
                        </div>
                    </Form>
                </Modal>
            </div>
        </div>
    );
};

export default CryptoSettings;
